import { useState, useEffect, useRef, useMemo } from "react";
import { NavLink, useLocation, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Offcanvas } from "react-bootstrap";

import {
  logoutUser,
  logoutAdmin,
} from "../../actions/reduxActions/userActions";

import { Skeleton } from "../ui/Skeleton";
import "./Header.scss";

const useWindowWidth = () => {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setW(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return w;
};

const useClickOutside = (ref, onOutside) => {
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onOutside();
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [ref, onOutside]);
};

const initialOf = (name) => {
  if (!name) return "?";
  const trimmed = String(name).trim();
  return trimmed ? trimmed[0].toUpperCase() : "?";
};

const Avatar = ({ user }) => {
  const url = user && user.avatar && user.avatar.url;
  if (url) {
    return (
      <span className='site-header__avatar'>
        <img src={url} alt={user.name || "Avatar"} />
      </span>
    );
  }
  return (
    <span className='site-header__avatar' aria-hidden='true'>
      {initialOf(user && user.name)}
    </span>
  );
};

const LanguageSwitcher = ({ t, i18n }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));

  const prefLang = i18n.languages[0] === "urdu" ? "urdu" : "en";

  const changeLang = (next) => {
    i18n.changeLanguage(next);
    window.localStorage.setItem("prefLanguage", JSON.stringify(next));
    setOpen(false);
  };

  return (
    <div className='site-header__lang' ref={ref}>
      <button
        type='button'
        className='site-header__lang-trigger'
        aria-expanded={open}
        aria-haspopup='menu'
        onClick={() => setOpen((v) => !v)}
      >
        <i className='bx bx-globe' aria-hidden='true'></i>
        <span>{prefLang === "en" ? "EN" : "اردو"}</span>
      </button>

      <div
        className={`site-header__menu site-header__lang-menu ${open ? "open" : ""}`}
        role='menu'
      >
        <button
          type='button'
          role='menuitemradio'
          aria-checked={prefLang === "en"}
          className={`site-header__lang-item ${prefLang === "en" ? "active" : ""}`}
          onClick={() => changeLang("en")}
        >
          <img src='assets/img/ukflag.webp' alt='' />
          <span>{t("navbar.lang_en")}</span>
          <i className='bx bx-check check' aria-hidden='true'></i>
        </button>
        <button
          type='button'
          role='menuitemradio'
          aria-checked={prefLang === "urdu"}
          className={`site-header__lang-item ${prefLang === "urdu" ? "active" : ""}`}
          onClick={() => changeLang("urdu")}
        >
          <img src='assets/img/pkflag.webp' alt='' />
          <span>{t("navbar.lang_urdu")}</span>
          <i className='bx bx-check check' aria-hidden='true'></i>
        </button>
      </div>
    </div>
  );
};

const UserMenu = ({ user, items, onLogout, rtl, t }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();
  useClickOutside(ref, () => setOpen(false));

  // Close on navigation.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <div className='site-header__user' ref={ref}>
      <button
        type='button'
        className='site-header__user-trigger'
        aria-expanded={open}
        aria-haspopup='menu'
        onClick={() => setOpen((v) => !v)}
      >
        <Avatar user={user} />
        <span className={`site-header__user-name ${rtl}`}>
          {user && user.name ? user.name : ""}
        </span>
        <i className='bx bx-chevron-down chev' aria-hidden='true'></i>
      </button>

      <div className={`site-header__menu ${open ? "open" : ""}`} role='menu'>
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end !== false}
            className={({ isActive }) =>
              `site-header__menu-item ${isActive ? "active" : ""} ${rtl}`
            }
            role='menuitem'
          >
            {item.icon && <i className={`bx ${item.icon}`} aria-hidden='true'></i>}
            <span>{item.label}</span>
          </NavLink>
        ))}
        <div className='site-header__menu-divider' role='separator' />
        <button
          type='button'
          className={`site-header__menu-item site-header__menu-item--danger ${rtl}`}
          role='menuitem'
          onClick={onLogout}
        >
          <i className='bx bx-log-out' aria-hidden='true'></i>
          <span>{t("navbar.link_logout_title") || "Logout"}</span>
        </button>
      </div>
    </div>
  );
};

const NavigationBar = ({ t, i18n }) => {
  const location = useLocation();
  const winWidth = useWindowWidth();
  const dispatch = useDispatch();
  const { user, isAuthenticated, loading } = useSelector((state) => state.user);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [adminOffcanvas, setAdminOffcanvas] = useState(false);

  const prefLang = i18n.languages[0] === "urdu" ? "urdu" : "en";
  const rtl = prefLang === "urdu" ? "text-right" : "";

  // Close the mobile collapse on navigation.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const logout = () => {
    if (user && user.role === "admin") {
      dispatch(logoutAdmin({ adminOut: true }));
    } else if (user && user.role === "user") {
      dispatch(logoutUser());
    }
  };

  // -- Link tables (single source of truth) --------------------------------

  // Shown while the session loads too, so the header doesn't pop in.
  const primaryLinks = useMemo(() => {
    if (user && user.role === "admin") {
      return [
        { to: "/", label: "Home", end: true },
        { to: "/contact", label: "Contact", end: true },
      ];
    }
    return [
      { to: "/", label: t("navbar.link_home_title"), end: true },
      { to: "/contact", label: t("navbar.link_contact_title"), end: true },
    ];
  }, [user, t]);

  const userMenuItems = useMemo(() => {
    if (!user) return [];
    if (user.role === "admin") {
      return [
        { to: "/admin/dashboard", label: "Dashboard", icon: "bx-grid-alt" },
        { to: "/admin/dashboard/listings", label: "All Listings", icon: "bx-list-ul" },
        { to: "/admin/manage-users", label: "Manage Users", icon: "bx-group" },
        { to: "/admin/inbox/1", label: "Inbox", icon: "bx-envelope" },
        { to: "/admin/contact-messages/1", label: "Contact Messages", icon: "bx-message-rounded-detail" },
        { to: "/admin/support-email", label: "Send Email", icon: "bx-send" },
        { to: "/admin/partner-agencies", label: "Partner Agencies", icon: "bx-buildings" },
        { to: "/admin/other-infos", label: "Site Info", icon: "bx-info-circle" },
        { to: "/admin/manage-property-types", label: "Property Types", icon: "bx-category" },
        { to: "/admin/manage-property-sub-types", label: "Property Sub-Types", icon: "bx-category-alt" },
        { to: "/admin/manage-other-property-details", label: "Other Details", icon: "bx-collection" },
        { to: "/admin/profile", label: "Profile", icon: "bx-user" },
      ];
    }
    return [
      {
        to: `/user/${user.username || ""}`,
        label: t("navbar.link_profile_title"),
        icon: "bx-user",
      },
      {
        to: "/user/listings/1",
        label: t("navbar.link_my_props_title"),
        icon: "bx-building",
      },
      {
        to: "/user/liked-listings/1",
        label: t("navbar.link_likes"),
        icon: "bx-heart",
      },
    ];
  }, [user, t]);

  // -- Render --------------------------------------------------------------

  const isAdmin = !loading && user && user.role === "admin" && isAuthenticated;
  const isUser = !loading && user && user.role === "user" && isAuthenticated;
  const isGuest = !loading && !isAuthenticated;

  // On mobile (≤767) the bottom tab bar shows Home/Listings/Saved/Profile, so
  // suppress those duplicates from the hamburger panel. Admin items aren't in
  // the tab bar, so admins keep their full menu.
  const tabbarVisible = winWidth <= 767;
  const panelUserMenuItems =
    tabbarVisible && isUser ? [] : userMenuItems;

  // The tab bar is fixed to the bottom of the viewport; the body class
  // reserves room for it so page content and FABs don't sit underneath.
  // Map search runs its own full-screen layout, so the bar stays off there.
  const showTabbar =
    tabbarVisible && !location.pathname.includes("/properties/");
  useEffect(() => {
    document.body.classList.toggle("has-tabbar", showTabbar);
    return () => document.body.classList.remove("has-tabbar");
  }, [showTabbar]);

  const guestEndLinks = (
    <div className='site-header__guest'>
      <NavLink
        to='/users/signup'
        className={({ isActive }) =>
          `site-header__link ${isActive ? "active" : ""} ${rtl}`
        }
        end
      >
        {t("navbar.link_signup_title")}
      </NavLink>
      <NavLink
        to='/users/login'
        className={({ isActive }) =>
          `site-header__link site-header__link--cta ${isActive ? "active" : ""} ${rtl}`
        }
      >
        {t("navbar.link_login_title")}
      </NavLink>
    </div>
  );

  return (
    <>
      <header className='site-header'>
        <div className='site-header__inner'>
          {isAdmin && (
            <button
              type='button'
              className='site-header__admin-toggle'
              onClick={() => setAdminOffcanvas(true)}
              aria-label='Admin menu'
            >
              <i className='bx bx-menu' aria-hidden='true'></i>
            </button>
          )}

          <Link to='/' className='site-header__brand' aria-label='PkPlots home'>
            <img src='assets/img/logo.svg' alt='PkPlots' />
            <div className='d-flex flex-column' title='Beta Testing'>
              <span
                style={{
                  backgroundColor: "#15803d",
                  color: "#fff",
                  fontSize: "12px",
                  borderRadius: "16%",
                  padding: "0 4px",
                  lineHeight: 1.2,
                }}
              >
                BETA
              </span>
            </div>
          </Link>

          {/* Desktop primary nav */}
          <nav className='site-header__nav-wrap' aria-label='Main'>
          <ul className='site-header__nav site-header__nav--primary'>
            {primaryLinks.map((l) => (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  end={l.end !== false}
                  className={({ isActive }) =>
                    `site-header__link ${isActive ? "active" : ""} ${rtl}`
                  }
                >
                  {l.label}
                </NavLink>
              </li>
            ))}
          </ul>
          </nav>

          {/* Desktop end nav */}
          <ul className='site-header__nav site-header__nav--end'>
            {isGuest && <li>{guestEndLinks}</li>}
            {!isAdmin && (
              <li>
                <LanguageSwitcher t={t} i18n={i18n} />
              </li>
            )}
            {loading && (
              <li className='site-header__user-pending' aria-hidden='true'>
                <Skeleton circle w='32px' h='32px' />
                <Skeleton w='4.5rem' h='0.8rem' />
              </li>
            )}
            {(isUser || isAdmin) && (
              <li>
                <UserMenu
                  user={user}
                  items={userMenuItems}
                  onLogout={logout}
                  rtl={rtl}
                  t={t}
                />
              </li>
            )}
          </ul>

          {/* Mobile hamburger */}
          <button
            type='button'
            className='site-header__hamburger'
            onClick={() => setMobileOpen((v) => !v)}
            aria-expanded={mobileOpen}
            aria-controls='site-mobile-panel'
            aria-label='Toggle menu'
          >
            <i className={`bx ${mobileOpen ? "bx-x" : "bx-menu"}`}></i>
          </button>
        </div>

        {/* Mobile collapse panel */}
        <div
          id='site-mobile-panel'
          className={`site-header__mobile-panel ${mobileOpen ? "open" : ""}`}
        >
          <ul className='site-header__mobile-list'>
            {primaryLinks.map((l) => (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  end={l.end !== false}
                  className={({ isActive }) =>
                    `site-header__menu-item ${isActive ? "active" : ""} ${rtl}`
                  }
                >
                  <span>{l.label}</span>
                </NavLink>
              </li>
            ))}
            {(isUser || isAdmin) &&
              panelUserMenuItems.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end !== false}
                    className={({ isActive }) =>
                      `site-header__menu-item ${isActive ? "active" : ""} ${rtl}`
                    }
                  >
                    {item.icon && (
                      <i className={`bx ${item.icon}`} aria-hidden='true'></i>
                    )}
                    <span>{item.label}</span>
                  </NavLink>
                </li>
              ))}
            {isGuest && (
              <>
                <li>
                  <NavLink
                    to='/users/signup'
                    className={({ isActive }) =>
                      `site-header__menu-item ${isActive ? "active" : ""} ${rtl}`
                    }
                  >
                    <i className='bx bx-user-plus' aria-hidden='true'></i>
                    <span>{t("navbar.link_signup_title")}</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to='/users/login'
                    className={({ isActive }) =>
                      `site-header__menu-item ${isActive ? "active" : ""} ${rtl}`
                    }
                  >
                    <i className='bx bx-log-in' aria-hidden='true'></i>
                    <span>{t("navbar.link_login_title")}</span>
                  </NavLink>
                </li>
              </>
            )}
            <li>
              <div style={{ padding: "0.5rem 0.65rem" }}>
                <LanguageSwitcher t={t} i18n={i18n} />
              </div>
            </li>
            {(isUser || isAdmin) && (
              <>
                <li>
                  <div
                    className='site-header__menu-divider'
                    role='separator'
                  />
                </li>
                <li>
                  <button
                    type='button'
                    className={`site-header__menu-item site-header__menu-item--danger ${rtl}`}
                    onClick={logout}
                  >
                    <i className='bx bx-log-out' aria-hidden='true'></i>
                    <span>{t("navbar.link_logout_title") || "Logout"}</span>
                  </button>
                </li>
              </>
            )}
          </ul>
        </div>

        {/* Admin offcanvas (mobile + nested admin pages) */}
        {isAdmin && (
          <Offcanvas
            show={adminOffcanvas}
            onHide={() => setAdminOffcanvas(false)}
          >
            <Offcanvas.Header
              className='bg-dark text-white'
              closeVariant='white'
              closeButton
            >
              <h4 className='d-flex align-items-center mx-auto m-0'>
                Admin Panel
              </h4>
            </Offcanvas.Header>
            <Offcanvas.Body>
              <ul className='site-header__mobile-list'>
                <li>
                  <NavLink
                    to='/admin/manage-property-types'
                    end
                    className={({ isActive }) =>
                      `site-header__menu-item ${isActive ? "active" : ""}`
                    }
                  >
                    <i className='bx bx-category' aria-hidden='true'></i>
                    <span>Manage Property Types</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to='/admin/manage-property-sub-types'
                    end
                    className={({ isActive }) =>
                      `site-header__menu-item ${isActive ? "active" : ""}`
                    }
                  >
                    <i className='bx bx-list-ul' aria-hidden='true'></i>
                    <span>Manage Property Sub Types</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to='/admin/manage-other-property-details'
                    end
                    className={({ isActive }) =>
                      `site-header__menu-item ${isActive ? "active" : ""}`
                    }
                  >
                    <i className='bx bx-detail' aria-hidden='true'></i>
                    <span>Manage Other Details</span>
                  </NavLink>
                </li>
              </ul>
            </Offcanvas.Body>
          </Offcanvas>
        )}
      </header>

      {/* Bottom tab bar (mobile only, suppressed on map-search detail routes) */}
      {showTabbar && (
          <nav className='site-tabbar' aria-label='Primary'>
            <NavLink
              to='/'
              end
              className={({ isActive }) =>
                `site-tabbar__item ${isActive ? "active" : ""}`
              }
              aria-label='home'
            >
              <i className='bx bx-home-alt-2' aria-hidden='true'></i>
              <span>Home</span>
            </NavLink>
            {isUser && (
              <>
                <NavLink
                  to='/user/listings/1'
                  end
                  className={({ isActive }) =>
                    `site-tabbar__item ${isActive ? "active" : ""}`
                  }
                  aria-label='listings'
                >
                  <i className='bx bx-building' aria-hidden='true'></i>
                  <span>{t("navbar.link_my_props_title")}</span>
                </NavLink>
                <NavLink
                  to='/user/liked-listings/1'
                  end
                  className={({ isActive }) =>
                    `site-tabbar__item ${isActive ? "active" : ""}`
                  }
                  aria-label='likes'
                >
                  <i className='bx bx-heart' aria-hidden='true'></i>
                  <span>{t("navbar.link_likes")}</span>
                </NavLink>
                <NavLink
                  to={`/user/${user && user.username}`}
                  end
                  className={({ isActive }) =>
                    `site-tabbar__item ${isActive ? "active" : ""}`
                  }
                  aria-label='profile'
                >
                  <i className='bx bx-user' aria-hidden='true'></i>
                  <span>{t("navbar.link_profile_title")}</span>
                </NavLink>
              </>
            )}
            {isAdmin && (
              <NavLink
                to='/admin/dashboard'
                end
                className={({ isActive }) =>
                  `site-tabbar__item ${isActive ? "active" : ""}`
                }
                aria-label='admin'
              >
                <i className='bx bx-user' aria-hidden='true'></i>
                <span>Admin</span>
              </NavLink>
            )}
            {isGuest && (
              <NavLink
                to='/users/login'
                end
                className={({ isActive }) =>
                  `site-tabbar__item ${isActive ? "active" : ""}`
                }
                aria-label='login'
              >
                <i className='bx bx-user' aria-hidden='true'></i>
                <span>{t("navbar.link_login_title")}</span>
              </NavLink>
            )}
          </nav>
        )}
    </>
  );
};

export default NavigationBar;
